/**
 * Utility functions for handling Venezuelan time zone (VET - UTC-4)
 */

/**
 * Get current date and time in Venezuelan time zone (VET - UTC-4)
 * @returns {Date} - Current date/time in Venezuelan timezone
 */
export const getVenezuelanTime = () => {
  // Venezuelan time is UTC-4
  const now = new Date();
  const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
  const venezuelanOffset = -4 * 60 * 60000; // UTC-4 in milliseconds
  return new Date(utcTime + venezuelanOffset);
};

/**
 * Check if a draw has already passed based on Venezuelan time
 * @param {string} drawTime - Draw time in format "HH:MM AM/PM" or "HH:MM:SS"
 * @param {string} closeTime - Close time in format "HH:MM:SS" (24-hour format)
 * @returns {boolean} - True if draw has passed (close time has passed), false otherwise
 */
export const isDrawPassed = (drawTime, closeTime) => {
  try {
    const venezuelanNow = getVenezuelanTime();

    // Primary method: Use closeTime if available (format: "HH:MM:SS")
    if (closeTime) {
      const timeMatch = closeTime.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (timeMatch) {
        const [, hours, minutes, seconds = "0"] = timeMatch;

        // Create date object for today with the close time
        const closeDate = new Date(venezuelanNow);
        closeDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), parseInt(seconds, 10), 0);

        // Check if current time is past the close time
        return venezuelanNow >= closeDate;
      }
    }

    // Fallback: parse the draw time string if closeTime is not available
    if (drawTime) {
      const timeMatch = drawTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (timeMatch) {
        let [, hours, minutes, period] = timeMatch;
        hours = parseInt(hours, 10);
        minutes = parseInt(minutes, 10);

        // Convert to 24-hour format if period is provided
        if (period) {
          const isPM = period.toUpperCase() === "PM";
          if (isPM && hours !== 12) {
            hours += 12;
          } else if (!isPM && hours === 12) {
            hours = 0;
          }
        }

        // Create date object for today with the draw time
        const drawDate = new Date(venezuelanNow);
        drawDate.setHours(hours, minutes, 0, 0);

        return venezuelanNow >= drawDate;
      }
    }

    return false;
  } catch (_error) {
    return false;
  }
};

/**
 * Format lottery draw time for UI display only (AM/PM).
 * Keeps underlying data in its original format for API/logic.
 * Accepts "HH:MM", "HH:MM:SS", "HH:MM AM/PM", or comma-separated lists.
 * @param {string} timeString
 * @returns {string}
 */
export const formatLotteryTimeAmPm = timeString => {
  if (!timeString || typeof timeString !== "string") return timeString ?? "";

  if (timeString.includes(",")) {
    return timeString
      .split(",")
      .map(part => formatLotteryTimeAmPm(part.trim()))
      .join(", ");
  }

  const trimmed = timeString.trim();

  const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampmMatch) {
    const hours = parseInt(ampmMatch[1], 10);
    const minutes = ampmMatch[2];
    const period = ampmMatch[3].toUpperCase();
    return `${String(hours).padStart(2, "0")}:${minutes} ${period}`;
  }

  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${String(hours).padStart(2, "0")}:${minutes} ${period}`;
};

/**
 * Format Venezuelan time to display format
 * @param {Date} date - Date object
 * @returns {string} - Formatted time string
 */
export const formatVenezuelanTime = (date = getVenezuelanTime()) => {
  return date.toLocaleTimeString("es-VE", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "America/Caracas",
  });
};
